/**
 * QuickBooks classes → properties. `apps/api/src/routes/qbo-classes.ts`, migration 0076.
 *
 * A line reaches a property's P&L only through its QuickBooks class. Each row is one class one
 * company file uses, with how many lines carry it; the property is suggested from the street
 * number and is the owner's to confirm. A mapping takes effect at the next daily sync.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api.js';

interface ClassRow {
  entityId: string;
  entityName: string;
  className: string;
  lines: number;
  propertyId: string | null;
  suggestedPropertyId: string | null;
}

interface ClassesResponse {
  properties: Array<{ id: string; name: string; entityId: string }>;
  classes: ClassRow[];
}

const rowKey = (c: ClassRow) => `${c.entityId}|${c.className}`;

export function ClassesPage(): ReactNode {
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const classes = useQuery({
    queryKey: ['finance', 'classes'],
    queryFn: () => api.get<ClassesResponse>('/api/finance/classes'),
  });

  const save = useMutation({
    mutationFn: async (rows: Array<{ row: ClassRow; propertyId: string | null }>) => {
      for (const { row, propertyId } of rows) {
        await api.put('/api/finance/classes', {
          entityId: row.entityId,
          className: row.className,
          propertyId,
        });
      }
      return rows.length;
    },
    onSuccess: (n) => {
      setError(null);
      setMessage(`Saved ${n} mapping${n === 1 ? '' : 's'}. Lines move at the next daily sync.`);
    },
    onError: (err: unknown) => {
      setMessage(null);
      setError(err instanceof ApiError ? err.message : String(err));
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['finance', 'classes'] }),
  });

  const data = classes.data;
  const propertyName = useMemo(
    () => new Map((data?.properties ?? []).map((p) => [p.id, p.name])),
    [data],
  );
  const selected = (c: ClassRow) =>
    choice[rowKey(c)] ?? c.propertyId ?? c.suggestedPropertyId ?? '';
  const ready = (data?.classes ?? []).filter((c) => !c.propertyId && selected(c));
  const unmapped = (data?.classes ?? []).filter((c) => !c.propertyId);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>QuickBooks classes</h1>
          <p className="subtitle">
            Which property each QuickBooks class means. A line with an unmapped class stays on the
            company's statement but on no property's.
          </p>
        </div>
      </header>

      {classes.isLoading && <p className="muted">Loading…</p>}
      {classes.isError && <p className="error">Could not load classes: {classes.error.message}</p>}
      {error && <p className="error">{error}</p>}
      {message && <div className="alert alert-ok">{message}</div>}

      {data && (
        <section className="card">
          <p>
            <strong>{unmapped.length}</strong> of {data.classes.length} classes unmapped,{' '}
            {unmapped.reduce((n, c) => n + c.lines, 0).toLocaleString()} lines.{' '}
            {ready.length > 0 && (
              <button
                type="button"
                disabled={save.isPending}
                onClick={() =>
                  save.mutate(ready.map((row) => ({ row, propertyId: selected(row) })))
                }
              >
                {save.isPending ? 'Saving…' : `Save ${ready.length} suggested`}
              </button>
            )}
          </p>
          {data.classes.length === 0 ? (
            <p className="empty">No QuickBooks line carries a class.</p>
          ) : (
            <table className="table compact">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Class</th>
                  <th className="num">Lines</th>
                  <th>Property</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.classes.map((c) => {
                  const value = selected(c);
                  return (
                    <tr key={rowKey(c)}>
                      <td className="muted">{c.entityName}</td>
                      <td>{c.className}</td>
                      <td className="num">{c.lines.toLocaleString()}</td>
                      <td>
                        <select
                          aria-label={`Property for ${c.className}`}
                          value={value}
                          onChange={(e) => setChoice({ ...choice, [rowKey(c)]: e.target.value })}
                        >
                          <option value="">Not a property</option>
                          {data.properties.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                              {p.id === c.suggestedPropertyId ? ' (suggested)' : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {c.propertyId && value === c.propertyId ? (
                          <span className="chip chip-ok">
                            {propertyName.get(c.propertyId) ?? 'Mapped'}
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={save.isPending || value === (c.propertyId ?? '')}
                            onClick={() => save.mutate([{ row: c, propertyId: value || null }])}
                          >
                            Save
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
